/**
 * Custom hook to handle JSON file upload
 * 
 * @returns {Function} - The function to handle JSON file upload.
 * 
 * @example
 * const handleFileUpload = useHandleFileUpload();
 * const fileInput = document.querySelector('input[type="file"]');
 * fileInput.addEventListener('change', (e) => handleFileUpload(e.target.files[0]));
 */
export function useHandleFileUpload() {

    /**
     * Handles the upload of a JSON file, reads its contents, and parses it as JSON.
     *
     * @param {File} file - The uploaded file object to be processed.
     * @returns {Promise<Object>} - A promise that resolves to the parsed JSON object.
     * @throws {Error} - Throws if the file is not a valid JSON file or if reading/parsing fails.
     */
    const handleFileUpload = async (file) => {
      try {
        // Check if the file is a valid JSON file
        if (file.type !== 'application/json') {
          throw new Error('Uploaded file is not a valid JSON file.');
        }
  
        // Read the file content asynchronously using FileReader
        const jsonData = await new Promise((resolve, reject) => {
          const fileReader = new FileReader();
  
          fileReader.onload = () => {
            try {
              // Parse the JSON data
              const data = JSON.parse(fileReader.result);
              resolve(data);
            } catch (error) {
              reject(new Error('Error parsing JSON file.'));
            }
          };
  
          fileReader.onerror = () => reject(new Error('Error reading file.'));
          fileReader.readAsText(file);
        });
  
        return jsonData;
  
      } catch (error) {
        console.error('Error handling file upload:', error);
        throw error;
      }
    };
  
    return handleFileUpload;
  }
  